import { motion } from 'framer-motion';
import { WordReveal } from '@/components/ui/word-reveal';

export function HowItWorks() {
  const steps = [
    {
      num: '01',
      title: 'Tell us what you need',
      body: 'Answer a few quick questions about your business or legal requirement.',
    },
    {
      num: '02',
      title: 'We prepare a draft',
      body: 'We collect required details, prepare the draft and keep you updated in chat.',
    },
    {
      num: '03',
      title: 'Approve and download',
      body: 'Review, request changes and approve — all in one seamless place.',
    },
  ];

  return (
    <section
      id="how"
      className="scroll-mt-20 bg-white py-20 md:py-28 border-t border-gray-100 relative"
    >
      <div className="mx-auto w-full max-w-7xl 2xl:max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#15803d]">
            HOW IT WORKS
          </span>
          <WordReveal
            as="h2"
            text="Three simple steps, from question to compliance"
            className="font-display text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl leading-tight"
            stagger={0.05}
          />
          <p className="text-base text-gray-600 font-normal">
            A smooth and transparent process designed for businesses and individuals.
          </p>
        </div>

        {/* Glassmorphic Step Cards with Connector Arrows */}
        <div className="mt-16 flex flex-col md:flex-row items-stretch justify-between gap-6 md:gap-6">
          {steps.map((s, idx) => (
            <div key={s.num} className="flex-1 w-full flex items-center gap-4">
              <motion.div
                initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
                whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                viewport={{ once: true, margin: '-10% 0px' }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="group relative flex-1 w-full rounded-2xl border border-gray-200/90 bg-white/90 p-6 sm:p-7 shadow-xs backdrop-blur-md hover:border-[#15803d]/70 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden space-y-4 min-h-[195px]"
              >
                {/* Background Ambient Glow Effect on Hover */}
                <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-emerald-500/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10 space-y-3.5">
                  <span className="inline-flex h-8 items-center justify-center rounded-lg bg-[#15803d] px-3 text-xs font-black text-white shadow-xs">
                    {s.num}
                  </span>

                  <div>
                    <h3 className="font-display text-lg font-bold text-gray-900 group-hover:text-[#15803d] transition-colors leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed font-normal">
                      {s.body}
                    </p>
                  </div>
                </div>
              </motion.div>

              {idx < steps.length - 1 && (
                <div className="hidden md:flex shrink-0 items-center justify-center text-[#15803d] font-bold px-1">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200/80 text-[#15803d] shadow-xs text-base">
                    →
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
